import { useMemo } from "react";
import {
    getOrderedCricketTeamLogoUrls,
    resolveCricketDefaultShieldUrl,
} from "../../../utils/cricketTeamLogoUrl";
import { resolveFirstLoadingImageUrl } from "../../../utils/resolveFirstLoadingImageUrl";
import { useResolvedImageSrc } from "../../../utils/useResolvedImageSrc";

export { getOrderedCricketTeamLogoUrls } from "../../../utils/cricketTeamLogoUrl";

/**
 * First cricket team logo URL that loads successfully, or null (no fallback shield).
 * @param {string} teamName
 * @returns {Promise<string | null>}
 */
export function resolveCricketTeamLogoUrl(teamName) {
    return resolveFirstLoadingImageUrl(getOrderedCricketTeamLogoUrls(teamName));
}

/**
 * Cricket team crest — flat S3 then legacy CDN.
 *
 * Prefers a session-cached URL (or the first candidate optimistically), then confirms
 * off-DOM so missing assets never flash the browser broken-image icon or the shield.
 */
export const CricketTeamLogo = ({
    teamName = "",
    height = "40px",
    width = "40px",
    fallbackOveride = "false",
    sport = "cricket",
    logo = null,
    className = "",
}) => {
    const fallbackImageUrl = resolveCricketDefaultShieldUrl(fallbackOveride);
    const orderedUrls = useMemo(() => getOrderedCricketTeamLogoUrls(teamName), [teamName]);
    const customLogo = typeof logo === "string" && logo.trim() ? logo.trim() : "";
    const candidateUrls = useMemo(() => {
        const urls = [];
        if (customLogo) urls.push(customLogo);
        for (const url of orderedUrls) {
            if (url && !urls.includes(url)) urls.push(url);
        }
        return urls;
    }, [customLogo, orderedUrls]);

    const { src, hidden, onError } = useResolvedImageSrc(candidateUrls, fallbackImageUrl);

    function handleLoad(event) {
        const img = event.target;
        if (img.naturalHeight > img.naturalWidth) {
            img.style.maxHeight = `${height}`;
        }
    }

    return (
        <div
            className={`flex items-center ${className}`}
            style={{ width: width, height: height, minWidth: width, minHeight: height }}
        >
            <img
                src={src}
                onError={onError}
                alt={teamName ? `${teamName} team logo` : "Team logo"}
                className={`w-[${width}]`}
                onLoad={handleLoad}
                style={{
                    width: width,
                    height: height,
                    maxWidth: width,
                    maxHeight: height,
                    ...(hidden ? { visibility: "hidden" } : {}),
                }}
                decoding="async"
            />
        </div>
    );
};

export default CricketTeamLogo;
